/**
 * StoreHeader — persistent store + fulfilment + ETA bar
 *
 * Renders inside the store layout route ($orderSource.$siteId.($table).tsx)
 * so it is visible on menu, checkout, payment, and success screens.
 *
 * Design spec §5:
 *   - Static by default (no idle animation).
 *   - On store / fulfilment / ETA *change*, the changed value does a quick
 *     blur-masked crossfade (~200ms) so updates are visible without jarring.
 *   - Tapping opens the unified multi-site StoreFinder (via openStoreFinder).
 *   - The chevron / pill gets :active scale(0.97).
 */

import { useContext } from "react";
import clsx from "clsx";
import { toZonedTime } from "date-fns-tz";
import { Link, useSearchParams } from "@remix-run/react";

import OrderContext from "~/contexts/OrderContext";
import MenuContext from "~/contexts/MenuContext";
import SiteContext from "~/contexts/SiteContext";
import ThemeContext from "~/contexts/ThemeContext";
import { OrderFulfillmentType, OrderSource } from "~/entities/orderEntities";
import { urlGen } from "~/helpers/routesHelpers";
import { fulfilmentAvailability } from "./storeHeaderFulfilment";
import { formatDay, formatTime } from "~/helpers/dateHelper";
import { useBlurCrossfade } from "~/hooks/useBlurCrossfade";
import LocationIcon from "~/icons/LocationIcon";
import TimeIcon from "~/icons/TimeIcon";
import CateringIcon from "~/icons/CateringIcon";
import DineInMenuDropdown from "./DineInMenuDropdown";

import styles from "./StoreHeader.module.css";

const StoreHeader: React.FC = () => {
  const { site } = useContext(SiteContext);
  const {
    orderType,
    orderSummary,
    openStoreFinder,
    openTimeSheet,
    asapWait,
    deliveryInfo,
    orderSource,
  } = useContext(OrderContext);
  const { template } = useContext(ThemeContext);
  const { menus, menu } = useContext(MenuContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // ── ETA string ────────────────────────────────────────────────────────
  const isDelivery = orderType?.fulfillmentType === OrderFulfillmentType.DELIVERY;

  // For delivery: prefer estimatedReadyTime when the order is placed, otherwise
  // fall back to asapWait for the delivery order type (same as pickup logic).
  // For pickup: use requestedReadyTime or asapWait.
  // Self-describing so the chip reads as a time, not a bare number:
  //  - A chosen slot is a fixed booking → "Scheduled · 1 Jun, 2:00 AM".
  //  - An ASAP wait is an estimate → "Ready in ~20 min" / "Arrives in ~20 min"
  //    (the ~ + "in … min" signals it's approximate; "Ready" only applies here).
  const verb = isDelivery ? "Arrives" : "Ready";
  let etaText: string;
  if (orderSummary.requestedReadyTime) {
    const zoned = toZonedTime(
      orderSummary.requestedReadyTime,
      site.timeZone,
    );
    // "Tonight" reads more naturally than "Today" for an evening slot (>= 6 PM).
    const dayLabel =
      formatDay(zoned) === "Today" && zoned.getHours() >= 18
        ? "Tonight"
        : formatDay(zoned);
    etaText = `Scheduled · ${dayLabel}, ${formatTime(zoned)}`;
  } else if (asapWait != null && asapWait > 0) {
    etaText = `${verb} in ~${asapWait} min`;
  } else {
    etaText = `${verb} ASAP`;
  }

  const fulfillmentLabel = isDelivery ? "Delivery" : "Pickup";

  // For delivery, the address is what the customer cares about — lead with it
  // ("Delivery to 3 Ponsonby Rd") like Sal's, since the serving store is
  // auto-assigned. Trim a trailing country off the formatted address.
  const deliveryAddress = isDelivery
    ? deliveryInfo?.address.address1?.trim().replace(/,\s*New Zealand$/i, "")
    : undefined;

  // ── Blur-masked crossfade triggers (spec §5) ──────────────────────────
  const storeChanging = useBlurCrossfade(site?.siteName);
  const etaChanging = useBlurCrossfade(etaText);
  const fulfillmentChanging = useBlurCrossfade(fulfillmentLabel);

  // ── Theme colours ─────────────────────────────────────────────────────
  const backgroundColor = template.infoBar.general.backgroundColour;
  const color = template.infoBar.general.fontColour;

  if (!site) return null;

  // Brand the active-segment fill from the theme (same accent the StoreFinder
  // uses), so it's visibly selected and matches the rest of the picker.
  const accent = template.global.button.primaryButtonColour;
  const accentFg = template.global.button.primaryButtonFontColour;
  const subtitle = deliveryAddress ?? site.siteName;

  // Dine-in (QR at a table): one slim bar. On the menu view (a menu is chosen
  // and the store has >1 menu) the left slot is a back-to-grid control + a menu
  // switcher dropdown — no redundant store name, the diner scanned the QR here.
  // On the landing / a single-menu store the left slot is the store name (entry
  // confirmation) with no switcher. The Table pill is always on the right.
  // (Design: docs/superpowers/specs/2026-07-23-qr-dinein-flow-design.md §4/§8.)
  if (orderSource === OrderSource.TABLE_ORDERING) {
    const tableNumber = orderSummary?.tableNumber;
    const multiMenu = (menus?.length ?? 0) > 1;
    const onMenuView = multiMenu && !!searchParams.get("menuCode");
    const backChevron = (
      <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="10 3 5 8 10 13" />
      </svg>
    );
    return (
      <div className={styles.wrapper} style={{ backgroundColor, color }}>
        <div className={styles.inner}>
          <div
            className={clsx(styles.tableRow, onMenuView && styles.tableRowMenu)}
          >
            {onMenuView ? (
              <>
                <button
                  type="button"
                  className={styles.dineBack}
                  aria-label="Back to menus"
                  onClick={() =>
                    setSearchParams((x) => {
                      x.delete("menuCode");
                      return x;
                    })
                  }
                >
                  {backChevron}
                </button>
                <DineInMenuDropdown
                  menus={menus ?? []}
                  currentCode={menu?.code}
                  accent={accent}
                />
              </>
            ) : (
              <>
                <LocationIcon />
                <span className={styles.tableStore}>{site.siteName}</span>
              </>
            )}
            {tableNumber && (
              <span
                className={styles.tablePill}
                style={{ background: accent, color: accentFg }}
              >
                Table {tableNumber}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Which fulfilments the *current order source* actually offers at this site.
  // Drives greying-out of the unavailable Pickup/Delivery segment (e.g. a
  // catering site that only does pickup) rather than leaving it tappable and
  // routing the customer into a store finder with no matching sites. The
  // active segment is always one of these, so it never ends up disabled.
  // A fulfilment is offered only when the current experience has an active
  // order type for it that is ALSO currently available. An order type can be
  // active yet "Not available currently" (no availability window configured) —
  // e.g. catering delivery at a store that only does catering pickup — and must
  // still grey out. serviceAvailability lists the order types that are actually
  // available right now. The selected fulfilment always counts as available so
  // it never greys itself.
  // Which fulfilments to offer vs grey for the current experience at this site.
  // See fulfilmentAvailability for the rules (current-source only, availability
  // gated, selected-never-greys, safe-degrade when availability data is absent).
  const { pickupAvailable, deliveryAvailable } = fulfilmentAvailability(
    site.orderTypes,
    site.serviceAvailability,
    orderType,
  );

  // Catering is reached deliberately — from the nav, or a marketing `/catering`
  // link — not via a mode toggle buried in the order bar. So the everyday flow
  // shows NO catering chip here (it would add a "mode" the 98% never want and
  // make the bar feel complex). Only the catering experience itself is labelled:
  // an accent status PILL stating the mode, plus a one-line context banner, with
  // a quiet way back to everyday ordering.
  const inCatering = orderSource === OrderSource.CATERING;
  const everydayHref = urlGen("Order", {
    orderSource: OrderSource.ONLINE,
    siteId: site.id,
  });

  return (
    <div
      className={styles.wrapper}
      style={{ backgroundColor, color }}
    >
      <div className={styles.inner}>
        {/* Top row: Pickup/Delivery toggle on the left, time chip on the right.
            The address/store subtitle goes below so it gets the full bar width
            to breathe (no more truncating to "Po..."). */}
        <div className={styles.topRow}>
        <div
          className={styles.segments}
          role="tablist"
          aria-label="Order type"
        >
          <button
            type="button"
            role="tab"
            aria-selected={!isDelivery}
            disabled={!pickupAvailable}
            className={clsx(
              styles.segment,
              !isDelivery && styles.segmentActive,
              !pickupAvailable && styles.segmentDisabled,
            )}
            style={
              !isDelivery
                ? { background: accent, color: accentFg }
                : undefined
            }
            onClick={() => openStoreFinder("PICKUP")}
          >
            Pickup
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isDelivery}
            disabled={!deliveryAvailable}
            className={clsx(
              styles.segment,
              isDelivery && styles.segmentActive,
              !deliveryAvailable && styles.segmentDisabled,
            )}
            style={
              isDelivery
                ? { background: accent, color: accentFg }
                : undefined
            }
            onClick={() => openStoreFinder("DELIVERY")}
          >
            Delivery
          </button>
        </div>

          <button
            type="button"
            className={styles.timeChip}
            onClick={openTimeSheet}
            aria-label={`${etaText}. Tap to change the time.`}
          >
            <TimeIcon color="currentColor" />
            <span
              className={clsx(
                styles.valueSlot,
                styles.etaLabel,
                etaChanging && styles.changing,
              )}
            >
              {etaText}
            </span>
          </button>
        </div>

        {/* Store + experience on one row: the store pill takes the width and
            the experience switch sits at the end as a matching chip, so the
            bar stays two clean rows instead of orphaning the switch. */}
        <div className={styles.bottomRow}>
          <button
            type="button"
            className={styles.subtitleButton}
            onClick={() => openStoreFinder()}
            aria-label={
              deliveryAddress
                ? `Delivering to ${deliveryAddress}. Tap to change.`
                : `${fulfillmentLabel} from ${site.siteName}. Tap to change store.`
            }
          >
            <LocationIcon />
            <span
              className={clsx(
                styles.subtitleText,
                (storeChanging || fulfillmentChanging) && styles.changing,
              )}
            >
              {subtitle}
            </span>
            <svg
              className={styles.chevron}
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="6 3 11 8 6 13" />
            </svg>
          </button>

          {inCatering && (
            <span className={styles.cateringPill}
              style={{ background: accent, color: accentFg }}
            >
              <CateringIcon className={styles.cateringIcon} color={accentFg} />
              <span>Catering</span>
            </span>
          )}
        </div>

        {/* Context line — tells first-timers what catering means here and how to
            get back. Only while in the catering experience. */}
        {inCatering && (
          <div className={styles.cateringContext}>
            <span>You&rsquo;re ordering Catering — order ahead for events.</span>
            <Link
              className={styles.cateringContextSwitch}
              to={everydayHref}
              prefetch="intent"
            >
              Switch to everyday ordering →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreHeader;
